import React from "react";
import { createRoot } from "react-dom/client";
import { Translation } from "react-i18next";
import App from "./App.tsx";
import "./index.css";
import "./i18n";
import { oneSignalService } from "./lib/onesignal";
import { logPlatformInfo, detectNativeApp, initializeNavigationInterceptor } from "./lib/platform";
import { supabase } from "./integrations/supabase/client";
import { consoleCapture } from "./lib/consoleCapture";
import { initSentry, ErrorBoundary } from "./lib/sentry";
import { detectAndApplyLanguage } from "./lib/languageDetection";
import { initializeExchangeRates } from "./lib/utils";

// Handle stale chunk errors after deployment (MIME type / ChunkLoadError)
// When a new version is deployed, old hashed chunk files no longer exist on the server.
// The catch-all rewrite in vercel.json serves index.html (text/html) instead of the .js file,
// causing "Failed to load module script" MIME type errors.
// This listener catches those errors and forces a clean reload exactly once.
window.addEventListener('vite:preloadError', (event) => {
  console.warn('[ChunkError] Stale chunk detected, reloading...', event);
  event.preventDefault();

  const RELOAD_KEY = 'chunk_error_reload';
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();

  // Prevent reload loops — only auto-reload once per 30 seconds
  if (lastReload && now - Number(lastReload) < 30000) {
    console.error('[ChunkError] Already reloaded recently, skipping to prevent loop');
    return;
  }

  sessionStorage.setItem(RELOAD_KEY, String(now));

  // Clear service worker caches that may have cached the HTML-as-JS response
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }

  // Force a hard reload to get the latest index.html with correct chunk references
  window.location.reload();
});

// Also catch dynamic import() failures that vite:preloadError may not cover
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (
    reason &&
    (reason.message?.includes('Failed to fetch dynamically imported module') ||
     reason.message?.includes('Importing a module script failed') ||
     reason.message?.includes('error loading dynamically imported module'))
  ) {
    console.warn('[ChunkError] Dynamic import failure detected, reloading...', reason.message);
    event.preventDefault();

    const RELOAD_KEY = 'chunk_error_reload';
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();

    if (lastReload && now - Number(lastReload) < 30000) {
      console.error('[ChunkError] Already reloaded recently, skipping to prevent loop');
      return;
    }

    sessionStorage.setItem(RELOAD_KEY, String(now));

    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    window.location.reload();
  }
});

// Initialize Sentry FIRST for error tracking
// This must happen before React renders to catch initialization errors
initSentry();

// Initialize console capture for debugging
// This must happen before any other code runs to capture all logs
consoleCapture; // Just reference it to ensure it's initialized

// Initialize navigation interceptor for native apps
// This ensures all external navigations use Natively.openExternalURL with external=true
if (typeof window !== 'undefined') {
  initializeNavigationInterceptor().then(() => {
    console.log('[App] Navigation interceptor ready');
  });
}

// Log platform detection info on startup (async)
if (typeof window !== 'undefined') {
  logPlatformInfo();
}

// Detect and apply device language on first launch
// This ensures the app displays in the user's preferred language from the start
if (typeof window !== 'undefined') {
  detectAndApplyLanguage().then((detectedLang) => {
    if (detectedLang) {
      console.log('[App] Applied device language:', detectedLang);
    }
  }).catch((error) => {
    console.error('[App] Language detection error:', error);
  });

  // Initialize exchange rates for currency conversion
  // This pre-fetches rates so formatCurrency can convert synchronously
  initializeExchangeRates().catch((error) => {
    console.warn('[App] Exchange rate initialization error:', error);
  });
}

/**
 * Handle deep link callbacks from native iOS app
 * This is called when the app is opened via a custom URL scheme (gitfit://auth/callback)
 * after OAuth authentication in external Safari browser
 */
const handleDeepLinkAuth = async () => {
  if (typeof window === 'undefined') return;

  const detection = await detectNativeApp();
  if (!detection.isNative) return; // Only for native apps

  // Listen for app becoming active (deep link redirect)
  // This event may be triggered by the native app wrapper
  const handleAppResume = async () => {
    console.log('[DeepLink] App resumed, checking for auth session...');

    try {
      // The Supabase client will automatically detect the session from the URL
      // We just need to trigger a session check
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('[DeepLink] Error getting session:', error);
        return;
      }

      if (session) {
        console.log('[DeepLink] Session found after deep link:', session.user.id);
        // The auth state change listeners will handle navigation
      } else {
        console.log('[DeepLink] No session found after resume');
      }
    } catch (error) {
      console.error('[DeepLink] Error handling deep link:', error);
    }
  };

  // Listen for various events that might indicate app resume
  document.addEventListener('resume', handleAppResume);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      handleAppResume();
    }
  });

  // Also check immediately on load in case we're loading from a deep link
  window.addEventListener('load', handleAppResume);

  console.log('[DeepLink] Deep link auth handler initialized');
};

// Initialize deep link handler for native apps
if (typeof window !== 'undefined') {
  handleDeepLinkAuth();
}

// OneSignal initialization is now deferred and only happens when:
// 1. User visits the Social tab (after 2s delay), OR
// 2. User completes their third app visit, OR
// 3. User manually enables notifications in Settings
// This provides better UX by not requesting permissions immediately on startup

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary
    fallback={({ error, componentStack, resetError }) => (
      <Translation>
        {(t) => (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h1>{t('ui.text.something_went_wrong')}</h1>
            <p>{t('ui.text.working_on_fix')}</p>
            <button onClick={resetError} style={{
              padding: '10px 20px',
              marginTop: '20px',
              cursor: 'pointer',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px'
            }}>
              Try again
            </button>
            {import.meta.env.MODE === 'development' && (
              <details style={{ marginTop: '20px', textAlign: 'left' }}>
                <summary>{t('ui.text.error_details')}</summary>
                <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                  {error?.toString()}
                  {componentStack}
                </pre>
              </details>
            )}
          </div>
        )}
      </Translation>
    )}
  >
    <App />
  </ErrorBoundary>
);
